// SPDX-License-Identifier: AGPL-3.0-or-later

export const CHANNEL_ICON_GROUPS = [
	'general',
	'community',
	'gaming',
	'media',
	'sports',
	'entertainment',
	'nature',
	'food',
	'travel',
	'education',
	'objects',
	'symbols',
] as const;

export type ChannelIconGroup = (typeof CHANNEL_ICON_GROUPS)[number];

type ChannelIconGroups = Readonly<Record<ChannelIconGroup, ReadonlyArray<string>>>;

// Solar icon names without the style suffix, by picker group; clients draw the bold glyph.
export const CHANNEL_ICON_SOLAR = {
	general: [
		'hashtag',
		'chat-round-dots',
		'chat-round',
		'chat-round-question-mark',
		'dialog',
		'bell-bing',
		'bell',
		'clipboard-list',
		'clipboard-text',
		'document-text',
		'info-circle',
		'question-circle',
		'help',
		'link',
		'pin',
		'global',
		'mention-circle',
		'home',
	],
	community: [
		'hand-shake',
		'users-group-rounded',
		'user-plus',
		'user-check',
		'user-id',
		'shield-user',
		'shield-check',
		'shield-star',
		'lock',
		'bot',
		'settings',
		'history',
		'verified-check',
		'crown',
		'gift',
		'confetti',
		'calendar',
		'ticket',
		'revote',
		'hand-heart',
	],
	gaming: [
		'gamepad',
		'gamepad-old',
		'ghost',
		'bomb',
		'target',
		'ufo',
		'magic-wand',
		'cup-star',
		'ranking',
		'medal-ribbon-star',
		'masks',
		'keyboard',
		'monitor',
		'cpu',
		'widget',
	],
	media: [
		'music-note',
		'headphones-round-sound',
		'microphone',
		'microphone-large',
		'podcast',
		'radio',
		'vinyl',
		'turntable',
		'videocamera-record',
		'clapperboard-play',
		'camera',
		'gallery',
		'palette',
		'paint-brush',
		'tv',
		'screencast',
		'play-stream',
	],
	sports: [
		'football',
		'basketball',
		'volleyball',
		'tennis',
		'rugby',
		'golf',
		'bowling',
		'dumbbell',
		'running',
		'swimming',
		'bicycling',
		'hiking',
		'skateboarding',
		'meditation',
	],
	entertainment: [
		'emoji-funny-circle',
		'smile-circle',
		'sad-circle',
		'ferris-wheel',
		'balloon',
		'incognito',
		'mask-happy',
	],
	nature: [
		'leaf',
		'cat',
		'paw',
		'sun',
		'moon',
		'moon-stars',
		'cloud',
		'cloud-rain',
		'snowflake',
		'cloud-bolt',
		'wind',
		'waterdrop',
		'planet',
	],
	food: ['cup-hot', 'chef-hat', 'donut-bitten', 'tea-cup', 'plate'],
	travel: ['map', 'map-point', 'compass', 'plane', 'car', 'bus', 'tram', 'suitcase', 'signpost', 'city'],
	education: [
		'book',
		'book-bookmark',
		'square-academic-cap',
		'code',
		'programming',
		'atom',
		'dna',
		'test-tube',
		'telescope',
		'calculator',
		'translation',
		'lightbulb',
		'case',
		'chart',
		'brain',
	],
	objects: [
		'cart',
		'shop',
		'tag-price',
		'sale',
		'wallet',
		'dollar',
		'money-bag',
		'card',
		'delivery',
		'smartphone',
		'laptop',
	],
	symbols: [
		'star',
		'stars',
		'heart',
		'fire',
		'bolt',
		'infinity',
		'check-circle',
		'danger-triangle',
		'forbidden-circle',
		'like',
		'medal-star',
		'lightning',
	],
} as const satisfies ChannelIconGroups;

// Fluent Emoji (Color) names as in microsoft/fluentui-emoji, kebab-cased (Iconify fluent-emoji set), by picker group.
export const CHANNEL_ICON_EMOJI = {
	general: [
		'speech-balloon',
		'left-speech-bubble',
		'thought-balloon',
		'speaking-head',
		'megaphone',
		'bell',
		'scroll',
		'page-facing-up',
		'memo',
		'clipboard',
		'pushpin',
		'link',
		'paperclip',
		'bookmark',
		'bookmark-tabs',
		'newspaper',
		'envelope',
		'inbox-tray',
		'postbox',
		'information',
		'red-question-mark',
		'red-exclamation-mark',
		'globe-with-meridians',
		'keycap-hashtag',
		'label',
	],
	community: [
		'waving-hand',
		'handshake',
		'clapping-hands',
		'raising-hands',
		'thumbs-up',
		'ok-hand',
		'victory-hand',
		'crossed-fingers',
		'writing-hand',
		'saluting-face',
		'people-hugging',
		'busts-in-silhouette',
		'bust-in-silhouette',
		'name-badge',
		'identification-card',
		'crown',
		'person-with-crown',
		'police-officer',
		'robot',
		'gear',
		'hammer-and-wrench',
		'shield',
		'locked',
		'unlocked',
		'locked-with-key',
		'key',
		'no-entry',
		'prohibited',
		'warning',
		'no-one-under-eighteen',
		'wrapped-gift',
		'party-popper',
		'birthday-cake',
		'calendar',
		'spiral-calendar',
		'ticket',
		'ballot-box-with-ballot',
		'bar-chart',
		'alarm-clock',
		'file-folder',
	],
	gaming: [
		'video-game',
		'joystick',
		'game-die',
		'joker',
		'spade-suit',
		'chess-pawn',
		'puzzle-piece',
		'pool-8-ball',
		'bullseye',
		'crossed-swords',
		'bow-and-arrow',
		'water-pistol',
		'military-helmet',
		'bomb',
		'collision',
		'alien-monster',
		'alien',
		'ghost',
		'skull',
		'dragon',
		't-rex',
		'brick',
		'wood',
		'pick',
		'hammer-and-pick',
		'gem-stone',
		'racing-car',
		'motorcycle',
		'person-mage',
		'ninja',
		'troll',
		'crystal-ball',
		'magic-wand',
		'castle',
		'mahjong-red-dragon',
		'teddy-bear',
		'rocket',
		'flying-saucer',
	],
	media: [
		'musical-note',
		'musical-notes',
		'musical-score',
		'headphone',
		'studio-microphone',
		'microphone',
		'radio',
		'guitar',
		'violin',
		'saxophone',
		'trumpet',
		'drum',
		'long-drum',
		'musical-keyboard',
		'accordion',
		'flute',
		'singer',
		'artist',
		'artist-palette',
		'paintbrush',
		'framed-picture',
		'camera',
		'video-camera',
		'movie-camera',
		'clapper-board',
		'television',
		'cinema',
		'red-circle',
		'performing-arts',
		'muted-speaker',
		'fountain-pen',
		'pencil',
	],
	sports: [
		'soccer-ball',
		'basketball',
		'volleyball',
		'american-football',
		'rugby-football',
		'baseball',
		'tennis',
		'ping-pong',
		'ice-hockey',
		'person-golfing',
		'bowling',
		'boxing-glove',
		'martial-arts-uniform',
		'goal-net',
		'running-shoe',
		'person-running',
		'person-biking',
		'person-swimming',
		'person-surfing',
		'person-lifting-weights',
		'flexed-biceps',
		'skier',
		'snowboarder',
		'skateboard',
		'fishing-pole',
		'horse-racing',
		'person-climbing',
		'stadium',
		'person-wrestling',
		'person-in-lotus-position',
	],
	entertainment: [
		'face-with-tears-of-joy',
		'rolling-on-the-floor-laughing',
		'smiling-face-with-sunglasses',
		'smiling-face-with-heart-eyes',
		'star-struck',
		'partying-face',
		'thinking-face',
		'nerd-face',
		'zany-face',
		'exploding-head',
		'clown-face',
		'cowboy-hat-face',
		'sleeping-face',
		'pleading-face',
		'loudly-crying-face',
		'angry-face',
		'face-with-rolling-eyes',
		'see-no-evil-monkey',
		'jack-o-lantern',
		'popcorn',
		'circus-tent',
		'ferris-wheel',
		'roller-coaster',
		'fireworks',
		'mirror-ball',
		'balloon',
		'sunglasses',
		'japanese-dolls',
		'dango',
		'unicorn',
		'person-superhero',
		'person-vampire',
		'person-zombie',
		'man-dancing',
		'zzz',
	],
	nature: [
		'dog-face',
		'cat-face',
		'fox',
		'wolf',
		'bear',
		'panda',
		'tiger-face',
		'lion',
		'horse-face',
		'frog',
		'monkey-face',
		'rabbit-face',
		'penguin',
		'eagle',
		'owl',
		'dove',
		'butterfly',
		'honeybee',
		'bug',
		'spider',
		'turtle',
		'snake',
		'whale',
		'dolphin',
		'shark',
		'octopus',
		'tropical-fish',
		'hedgehog',
		'elephant',
		'paw-prints',
		'seedling',
		'deciduous-tree',
		'evergreen-tree',
		'palm-tree',
		'cactus',
		'four-leaf-clover',
		'fallen-leaf',
		'brown-mushroom',
		'cherry-blossom',
		'rose',
		'sunflower',
		'tulip',
		'sun',
		'cloud',
		'cloud-with-rain',
		'cloud-with-lightning',
		'snowflake',
		'rainbow',
		'droplet',
		'water-wave',
		'volcano',
		'crescent-moon',
		'milky-way',
		'ringed-planet',
		'globe-showing-europe-africa',
		'mountain',
		'desert-island',
	],
	food: [
		'pizza',
		'hamburger',
		'french-fries',
		'hot-dog',
		'burrito',
		'stuffed-flatbread',
		'sandwich',
		'doughnut',
		'cookie',
		'shortcake',
		'ice-cream',
		'chocolate-bar',
		'sushi',
		'steaming-bowl',
		'spaghetti',
		'cooked-rice',
		'dumpling',
		'meat-on-bone',
		'egg',
		'cooking',
		'croissant',
		'bread',
		'pretzel',
		'cheese-wedge',
		'green-salad',
		'red-apple',
		'banana',
		'grapes',
		'watermelon',
		'strawberry',
		'cherries',
		'lemon',
		'pineapple',
		'hot-pepper',
		'hot-beverage',
		'teacup-without-handle',
		'teapot',
		'bubble-tea',
		'cup-with-straw',
		'fork-and-knife-with-plate',
		'fork-and-knife',
		'honey-pot',
		'takeout-box',
	],
	travel: [
		'world-map',
		'compass',
		'airplane',
		'helicopter',
		'automobile',
		'taxi',
		'bus',
		'ambulance',
		'bicycle',
		'high-speed-train',
		'metro',
		'tram',
		'ship',
		'sailboat',
		'anchor',
		'luggage',
		'beach-with-umbrella',
		'camping',
		'house',
		'houses',
		'office-building',
		'classical-building',
		'cityscape',
		'night-with-stars',
		'bridge-at-night',
		'tokyo-tower',
		'mount-fuji',
		'hospital',
		'hotel',
		'department-store',
		'bank',
		'construction',
	],
	education: [
		'books',
		'open-book',
		'closed-book',
		'notebook',
		'graduation-cap',
		'school',
		'student',
		'teacher',
		'scientist',
		'technologist',
		'laptop',
		'desktop-computer',
		'floppy-disk',
		'mobile-phone',
		'satellite',
		'light-bulb',
		'atom-symbol',
		'dna',
		'microscope',
		'telescope',
		'test-tube',
		'abacus',
		'triangular-ruler',
		'straight-ruler',
		'magnifying-glass-tilted-left',
		'brain',
		'briefcase',
		'chart-increasing',
		'chart-decreasing',
		'hammer',
		'input-latin-letters',
		'globe-showing-americas',
	],
	objects: [
		'shopping-cart',
		'shopping-bags',
		'handbag',
		'package',
		'receipt',
		'credit-card',
		'coin',
		'money-bag',
		'money-with-wings',
		'dollar-banknote',
		'euro-banknote',
		'currency-exchange',
		'ring',
		'delivery-truck',
		't-shirt',
		'billed-cap',
		'glasses',
		'watch',
		'backpack',
		'door',
		'couch-and-lamp',
		'bed',
		'broom',
		'hourglass-not-done',
	],
	symbols: [
		'star',
		'glowing-star',
		'sparkles',
		'fire',
		'high-voltage',
		'hundred-points',
		'red-heart',
		'orange-heart',
		'yellow-heart',
		'green-heart',
		'blue-heart',
		'purple-heart',
		'black-heart',
		'white-heart',
		'pink-heart',
		'sparkling-heart',
		'beating-heart',
		'revolving-hearts',
		'heart-with-arrow',
		'heart-with-ribbon',
		'heart-on-fire',
		'broken-heart',
		'mending-heart',
		'trophy',
		'1st-place-medal',
		'2nd-place-medal',
		'3rd-place-medal',
		'sports-medal',
		'rosette',
		'ribbon',
		'check-mark-button',
		'cross-mark',
		'infinity',
		'recycling-symbol',
		'green-circle',
		'large-orange-diamond',
		'sos-button',
		'new-button',
		'free-button',
		'vs-button',
		'dashing-away',
		'eight-pointed-star',
	],
} as const satisfies ChannelIconGroups;

// Phosphor icon names by picker group; each one comes in the regular, fill and duotone weights.
export const CHANNEL_ICON_PHOSPHOR = {
	general: [
		'hash',
		'chat',
		'chats',
		'chat-circle',
		'chat-circle-dots',
		'chat-circle-text',
		'chat-centered-text',
		'chat-teardrop-dots',
		'chat-teardrop-text',
		'chats-circle',
		'chats-teardrop',
		'megaphone',
		'megaphone-simple',
		'bell',
		'bell-ringing',
		'bell-simple-ringing',
		'notification',
		'scroll',
		'clipboard-text',
		'list-checks',
		'list-bullets',
		'list-numbers',
		'article',
		'newspaper',
		'newspaper-clipping',
		'note',
		'notepad',
		'note-pencil',
		'info',
		'question',
		'warning',
		'warning-circle',
		'seal-question',
		'link',
		'link-simple',
		'paperclip',
		'push-pin',
		'push-pin-simple',
		'at',
		'house',
		'house-line',
		'door-open',
		'signpost',
		'quotes',
		'envelope',
		'envelope-open',
		'mailbox',
		'paper-plane-tilt',
		'rss',
		'broadcast',
		'translate',
		'archive',
		'bookmark-simple',
		'bookmarks',
		'tag',
		'lightbulb',
		'lightbulb-filament',
		'magnifying-glass',
		'funnel',
	],
	community: [
		'handshake',
		'hand-waving',
		'users',
		'users-three',
		'users-four',
		'user',
		'user-plus',
		'user-check',
		'user-circle',
		'user-list',
		'user-gear',
		'user-focus',
		'user-sound',
		'identification-card',
		'identification-badge',
		'shield',
		'shield-check',
		'shield-star',
		'shield-checkered',
		'shield-warning',
		'lock',
		'lock-key',
		'key',
		'robot',
		'gear',
		'gear-six',
		'wrench',
		'clock-counter-clockwise',
		'seal-check',
		'crown',
		'crown-simple',
		'gift',
		'confetti',
		'calendar',
		'calendar-star',
		'calendar-check',
		'calendar-heart',
		'ticket',
		'lifebuoy',
		'hand-heart',
		'hand-coins',
		'heart',
		'thumbs-up',
		'thumbs-down',
		'hands-clapping',
		'ranking',
		'chart-bar',
		'chart-pie',
		'check-square',
		'prohibit',
		'gavel',
		'scales',
		'siren',
		'detective',
		'mask-happy',
	],
	gaming: [
		'game-controller',
		'joystick',
		'sword',
		'axe',
		'target',
		'crosshair',
		'crosshair-simple',
		'bomb',
		'skull',
		'ghost',
		'alien',
		'flying-saucer',
		'magic-wand',
		'treasure-chest',
		'puzzle-piece',
		'cube',
		'lego',
		'lego-smiley',
		'dice-five',
		'dice-six',
		'cards',
		'cards-three',
		'poker-chip',
		'spade',
		'club',
		'diamond',
		'checkerboard',
		'strategy',
		'castle-turret',
		'medal',
		'medal-military',
		'trophy',
		'keyboard',
		'mouse',
		'headset',
		'monitor',
		'desktop-tower',
		'cpu',
		'graphics-card',
		'virtual-reality',
		'sneaker-move',
		'lightning',
		'fire',
		'fire-simple',
		'rocket',
		'rocket-launch',
		'planet',
		'shooting-star',
	],
	media: [
		'music-note',
		'music-notes',
		'music-notes-simple',
		'playlist',
		'headphones',
		'microphone',
		'microphone-stage',
		'speaker-high',
		'speaker-hifi',
		'waveform',
		'equalizer',
		'radio',
		'vinyl-record',
		'disc',
		'cassette-tape',
		'piano-keys',
		'guitar',
		'metronome',
		'camera',
		'camera-plus',
		'image',
		'images',
		'video-camera',
		'film-strip',
		'film-slate',
		'film-reel',
		'monitor-play',
		'television',
		'screencast',
		'play',
		'record',
		'palette',
		'paint-brush',
		'paint-roller',
		'pencil-simple',
		'pen-nib',
		'scribble',
		'swatches',
		'sticker',
		'gif',
		'smiley-sticker',
	],
	sports: [
		'soccer-ball',
		'football',
		'basketball',
		'court-basketball',
		'volleyball',
		'tennis-ball',
		'racquet',
		'ping-pong',
		'baseball',
		'golf',
		'bowling-ball',
		'hockey',
		'cricket',
		'boxing-glove',
		'barbell',
		'person-simple-run',
		'person-simple-bike',
		'person-simple-swim',
		'person-simple-ski',
		'person-simple-snowboard',
		'person-simple-hike',
		'person-simple-tai-chi',
		'swimming-pool',
		'sneaker',
		'bicycle',
		'goggles',
		'steering-wheel',
		'timer',
		'heartbeat',
	],
	entertainment: [
		'popcorn',
		'mask-sad',
		'smiley',
		'smiley-wink',
		'smiley-melting',
		'smiley-angry',
		'smiley-sad',
		'smiley-nervous',
		'balloon',
		'cake',
		'champagne',
		'disco-ball',
		'star',
		'sparkle',
		'heart-straight',
		'heart-half',
		'heart-break',
		'eyes',
		'eye',
		'book-open',
		'books',
		'television-simple',
		'cat',
		'dog',
		'paw-print',
		'butterfly',
		'yarn',
		'pinwheel',
	],
	nature: [
		'tree',
		'tree-evergreen',
		'tree-palm',
		'plant',
		'potted-plant',
		'leaf',
		'flower',
		'flower-lotus',
		'flower-tulip',
		'cactus',
		'clover',
		'acorn',
		'mountains',
		'waves',
		'drop',
		'snowflake',
		'sun',
		'sun-horizon',
		'moon',
		'moon-stars',
		'cloud',
		'cloud-rain',
		'cloud-lightning',
		'rainbow',
		'tornado',
		'wind',
		'campfire',
		'tent',
		'bird',
		'fish',
		'rabbit',
		'horse',
		'cow',
		'bug',
		'atom',
		'globe-hemisphere-east',
		'island',
	],
	food: [
		'coffee',
		'tea-bag',
		'pizza',
		'hamburger',
		'bowl-food',
		'bowl-steam',
		'fork-knife',
		'cooking-pot',
		'chef-hat',
		'oven',
		'cookie',
		'ice-cream',
		'popsicle',
		'bread',
		'cheese',
		'egg',
		'carrot',
		'avocado',
		'orange',
		'orange-slice',
		'cherries',
		'pepper',
		'grains',
		'shrimp',
		'onigiri',
		'knife',
		'jar',
	],
	travel: [
		'airplane',
		'airplane-takeoff',
		'airplane-tilt',
		'car',
		'car-profile',
		'jeep',
		'motorcycle',
		'scooter',
		'bus',
		'train',
		'subway',
		'tram',
		'taxi',
		'boat',
		'sailboat',
		'anchor',
		'map-trifold',
		'map-pin',
		'compass',
		'globe',
		'suitcase',
		'suitcase-rolling',
		'backpack',
		'city',
		'buildings',
		'building-office',
		'bridge',
		'lighthouse',
		'storefront',
		'park',
		'gas-pump',
		'road-horizon',
	],
	education: [
		'graduation-cap',
		'student',
		'chalkboard-teacher',
		'chalkboard',
		'book',
		'book-open-text',
		'book-bookmark',
		'notebook',
		'exam',
		'pencil',
		'pencil-ruler',
		'calculator',
		'math-operations',
		'function',
		'sigma',
		'pi',
		'flask',
		'test-tube',
		'microscope',
		'dna',
		'brain',
		'globe-stand',
		'code',
		'code-block',
		'terminal-window',
		'bug-beetle',
		'git-branch',
		'database',
		'laptop',
		'desktop',
		'device-mobile',
		'circuitry',
		'briefcase',
		'chart-line-up',
		'presentation-chart',
		'lectern',
		'certificate',
	],
	objects: [
		'shopping-cart',
		'shopping-bag',
		'handbag',
		'package',
		'truck',
		'receipt',
		'credit-card',
		'wallet',
		'money',
		'coins',
		'coin',
		'piggy-bank',
		'bank',
		'cash-register',
		'tag-simple',
		't-shirt',
		'hoodie',
		'dress',
		'baseball-cap',
		'eyeglasses',
		'sunglasses',
		'watch',
		'armchair',
		'couch',
		'bed',
		'lamp',
		'door',
		'broom',
		'toolbox',
		'hammer',
		'screwdriver',
		'paint-bucket',
		'magnet',
		'hourglass',
		'alarm',
		'clock',
		'phone',
		'umbrella',
		'pill',
		'first-aid-kit',
		'stethoscope',
	],
	symbols: [
		'star-four',
		'infinity',
		'check-circle',
		'x-circle',
		'plus-circle',
		'seal',
		'seal-percent',
		'seal-warning',
		'warning-diamond',
		'exclamation-mark',
		'question-mark',
		'hash-straight',
		'asterisk',
		'percent',
		'currency-dollar',
		'currency-eur',
		'currency-btc',
		'trend-up',
		'trend-down',
		'peace',
		'recycle',
		'radioactive',
		'biohazard',
		'circle',
		'square',
		'triangle',
		'hexagon',
		'octagon',
		'shapes',
		'spiral',
		'cube-transparent',
		'number-circle-one',
		'number-circle-two',
		'number-circle-three',
		'dot-outline',
	],
} as const satisfies ChannelIconGroups;

export const CHANNEL_ICON_PHOSPHOR_WEIGHTS = ['regular', 'fill', 'duotone'] as const;

export type ChannelIconPhosphorWeight = (typeof CHANNEL_ICON_PHOSPHOR_WEIGHTS)[number];
export type ChannelIconPhosphorName = (typeof CHANNEL_ICON_PHOSPHOR)[ChannelIconGroup][number];
export type ChannelIconSolarKey = `s:${(typeof CHANNEL_ICON_SOLAR)[ChannelIconGroup][number]}`;
export type ChannelIconEmojiKey = `e:${(typeof CHANNEL_ICON_EMOJI)[ChannelIconGroup][number]}`;
export type ChannelIconPhosphorKey =
	| `p:${ChannelIconPhosphorName}`
	| `p:${ChannelIconPhosphorName}-${Exclude<ChannelIconPhosphorWeight, 'regular'>}`;
export type ChannelIconKey = ChannelIconSolarKey | ChannelIconEmojiKey | ChannelIconPhosphorKey;

function prefixedKeys<Key extends ChannelIconKey>(prefix: 's:' | 'e:', groups: ChannelIconGroups): Array<Key> {
	return CHANNEL_ICON_GROUPS.flatMap((group) => groups[group].map((name) => `${prefix}${name}` as Key));
}

export const CHANNEL_ICON_SOLAR_KEYS: ReadonlyArray<ChannelIconSolarKey> = prefixedKeys('s:', CHANNEL_ICON_SOLAR);
export const CHANNEL_ICON_EMOJI_KEYS: ReadonlyArray<ChannelIconEmojiKey> = prefixedKeys('e:', CHANNEL_ICON_EMOJI);

export function phosphorChannelIconKey(
	name: ChannelIconPhosphorName,
	weight: ChannelIconPhosphorWeight,
): ChannelIconPhosphorKey {
	return weight === 'regular' ? `p:${name}` : `p:${name}-${weight}`;
}

export const CHANNEL_ICON_PHOSPHOR_KEYS: ReadonlyArray<ChannelIconPhosphorKey> = CHANNEL_ICON_PHOSPHOR_WEIGHTS.flatMap(
	(weight) =>
		CHANNEL_ICON_GROUPS.flatMap((group) =>
			CHANNEL_ICON_PHOSPHOR[group].map((name) => phosphorChannelIconKey(name, weight)),
		),
);

export const CHANNEL_ICON_KEYS = [
	...CHANNEL_ICON_SOLAR_KEYS,
	...CHANNEL_ICON_EMOJI_KEYS,
	...CHANNEL_ICON_PHOSPHOR_KEYS,
] as unknown as readonly [ChannelIconKey, ...Array<ChannelIconKey>];

const CHANNEL_ICON_KEY_SET: ReadonlySet<string> = new Set(CHANNEL_ICON_KEYS);

// Index is the stored icon_color value; Fluent Emoji keys ignore it. Solar and Phosphor icons are tinted.
// currentColor draws the icon in the row's own color, the way Fluxer draws its # icon.
export const CHANNEL_ICON_COLORS = [
	'#A78BFA',
	'#60A5FA',
	'#22D3EE',
	'#4ADE80',
	'#FACC15',
	'#FB923C',
	'#F87171',
	'#F472B6',
	'#EF4444',
	'#EA580C',
	'#D97706',
	'#65A30D',
	'#16A34A',
	'#0D9488',
	'#0891B2',
	'#0284C7',
	'#3B82F6',
	'#6366F1',
	'#8B5CF6',
	'#A855F7',
	'#D946EF',
	'#EC4899',
	'#9A7B4F',
	'#848C98',
	'currentColor',
] as const;

export const CHANNEL_ICON_APP_COLOR = CHANNEL_ICON_COLORS.indexOf('currentColor');

export const CHANNEL_ICON_COLOR_MAX = CHANNEL_ICON_COLORS.length - 1;

export function isChannelIconKey(value: unknown): value is ChannelIconKey {
	return typeof value === 'string' && CHANNEL_ICON_KEY_SET.has(value);
}

export function channelIconColorFor(key: string | null, color: number | null): number | null {
	return key != null && !key.startsWith('e:') && CHANNEL_ICON_KEY_SET.has(key) ? color : null;
}
